import React, { ChangeEvent, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
    FormProvider,
    useForm,
} from "react-hook-form";

import { AppCard } from "../../component/AppCard";

import AppInput_New from "../../component_New/AppInput_New/AppInput_New";
import AppSelect_New from "../../component_New/AppSelect_New/AppSelect_New";
import AppDatePicker_New from "../../component_New/AppDatePicker_New/AppDatePicker_New";
import AppButton_New from "../../component_New/AppButton_New/AppButton_New";
import AppRowInput_New, {
    AppRowInputSection,
} from "../../component_New/AppRowInput_New/AppRowInput_New";
import AppDataTable_New, {
    AppDataTableBadge,
    AppDataTableColumn,
} from "../../component_New/AppDataTable_New/AppDataTable_New";
import AppTextBox_New from "../../component_New/AppTextBox_New/AppTextBox_New";
import AppMultiSelect_New, {
    AppMultiSelectOption,
} from "../../component_New/AppMultiSelect_New/AppMultiSelect_New";

interface NewComponentFormValues {
    dateOfBirth: Date | null;
    testResults: Record<string, unknown>;
}

const initialTestResultSections: AppRowInputSection[] = [
    {
        id: "rpr",
        title: "RPR Test Information",
        rows: [
            {
                id: "rpr-1",
                specimenCollectionDate: null,
                specimenId: "",
                details: "",
                titer: "",
            },
        ],
    },
    {
        id: "csfVdrl",
        title: "CSF-VDRL Test Information",
        rows: [
            {
                id: "csf-vdrl-1",
                specimenCollectionDate: null,
                specimenId: "",
                details: "",
                titer: "",
            },
        ],
    },
    {
        id: "tpPa",
        title: "TP-PA Information",
        rows: [],
    },
];

interface HealthcareLookupRecord {
    id: number;
    hospital: string;
    facility: string;
    providerMrn: string;
    eharsId?: string;
    starsProfileId?: string;
    recordStatus: "New" | "Updated";
    linkageStatus: string;
    pendingDays?: number;
    createdBy: string;
    createdDate: string;
    updatedBy?: string;
    updatedDate?: string;
}

const lookupRecords: HealthcareLookupRecord[] = [
    { id: 1, hospital: "Hospital A", facility: "Facility A1", providerMrn: "000001", eharsId: "SAMPLE001", recordStatus: "Updated", linkageStatus: "HIV Negative", createdBy: "Jane Doe", createdDate: "08/01/2026", updatedBy: "Alex Smith", updatedDate: "08/20/2026" },
    { id: 2, hospital: "Hospital A", facility: "Facility A1", providerMrn: "000002", recordStatus: "New", linkageStatus: "Already in Care", createdBy: "Jane Doe", createdDate: "08/05/2026" },
    { id: 3, hospital: "Hospital A", facility: "Facility A1", providerMrn: "000003", recordStatus: "New", linkageStatus: "Referred to EHE", createdBy: "Jane Doe", createdDate: "08/07/2026" },
    { id: 4, hospital: "Hospital B", facility: "Facility B1", providerMrn: "000004", starsProfileId: "100004", recordStatus: "Updated", linkageStatus: "Referred to EHE", createdBy: "John Smith", createdDate: "07/30/2026", updatedBy: "Jane Doe", updatedDate: "08/18/2026" },
    { id: 5, hospital: "Hospital C", facility: "Facility C1", providerMrn: "000005", starsProfileId: "100005", recordStatus: "Updated", linkageStatus: "Referred to EHE", createdBy: "Jane Doe", createdDate: "08/02/2026", updatedBy: "Jane Doe", updatedDate: "08/19/2026" },
    { id: 6, hospital: "Hospital B", facility: "Facility B1", providerMrn: "000006", recordStatus: "New", linkageStatus: "NA", createdBy: "Alex Smith", createdDate: "08/10/2026" },
    { id: 7, hospital: "Hospital A", facility: "Facility A2", providerMrn: "000007", recordStatus: "New", linkageStatus: "HIV Negative", createdBy: "Jane Doe", createdDate: "08/11/2026" },
    { id: 8, hospital: "Hospital B", facility: "Facility B1", providerMrn: "000008", recordStatus: "New", linkageStatus: "Pending", pendingDays: 8, createdBy: "John Smith", createdDate: "08/16/2026" },
    { id: 9, hospital: "Hospital A", facility: "Facility A1", providerMrn: "000009", recordStatus: "New", linkageStatus: "Out of care, referred to Ryan White Program", createdBy: "Jane Doe", createdDate: "08/12/2026" },
    { id: 10, hospital: "Hospital D", facility: "Facility D1", providerMrn: "000010", starsProfileId: "100010", recordStatus: "Updated", linkageStatus: "Others", createdBy: "Alex Smith", createdDate: "08/01/2026", updatedBy: "John Smith", updatedDate: "08/21/2026" },
    { id: 11, hospital: "Hospital A", facility: "Facility A3", providerMrn: "000011", recordStatus: "New", linkageStatus: "Pending", pendingDays: 19, createdBy: "Jane Doe", createdDate: "08/03/2026" },
];

const lookupColumns: AppDataTableColumn<HealthcareLookupRecord>[] = [
    {
        key: "location",
        header: "Hospital/Location",
        width: "15%",
        getSearchValue: (row) => `${row.hospital} ${row.facility}`,
        render: (row) => (
            <div>
                <strong style={{ display: "block" }}>{row.hospital}</strong>
                <small style={{ color: "#94a3b8", paddingLeft: "0.5rem" }}>- {row.facility}</small>
            </div>
        ),
    },
    { key: "providerMrn", header: "Provider MRN", getSearchValue: (row) => row.providerMrn, render: (row) => row.providerMrn },
    { key: "eharsId", header: "eHARS #", getSearchValue: (row) => row.eharsId, render: (row) => row.eharsId || "-" },
    { key: "starsProfileId", header: "STARS Profile ID", getSearchValue: (row) => row.starsProfileId, render: (row) => row.starsProfileId || "-" },
    {
        key: "recordStatus",
        header: "Rec Status",
        getSearchValue: (row) => row.recordStatus,
        render: (row) => <AppDataTableBadge tone={row.recordStatus === "Updated" ? "info" : "neutral"}>{row.recordStatus}</AppDataTableBadge>,
    },
    {
        key: "linkageStatus",
        header: "Linkage Status",
        getSearchValue: (row) => row.linkageStatus,
        render: (row) => row.linkageStatus === "Pending"
            ? <AppDataTableBadge tone={(row.pendingDays ?? 0) < 14 ? "warning" : "danger"}>Pending</AppDataTableBadge>
            : row.linkageStatus,
    },
];

const screeningTypeOptions: AppMultiSelectOption[] = [
    { value: "autism", label: "Autism" },
    { value: "dental", label: "Dental" },
    { value: "cholesterol", label: "Cholesterol" },
    { value: "colorectal-cancer", label: "Colorectal Cancer" },
    { value: "copd", label: "COPD / Other Lung Disease" },
    { value: "diabetes", label: "Diabetes" },
    { value: "hearing", label: "Hearing" },
    { value: "heart-disease", label: "Heart Disease" },
    { value: "hepatitis", label: "Hepatitis" },
    { value: "hiv", label: "HIV" },
];

const NewComponent: React.FC = () => {
    /* =========================================================
       React Hook Form
    ========================================================= */

    const formMethods =
        useForm<NewComponentFormValues>({
            defaultValues: {
                dateOfBirth: new Date(
                    2026,
                    7,
                    5
                ),
                testResults: {},
            },
        });

    /* =========================================================
       Input / Select state
    ========================================================= */

    const [source, setSource] =
        useState("Ehars");

    const [firstName, setFirstName] =
        useState("Jane");

    const [middleName, setMiddleName] =
        useState("");

    const [lastName, setLastName] =
        useState("Doe");

    const [birthSex, setBirthSex] =
        useState("Female");

    const [hospital, setHospital] =
        useState("Hospital A");

    const [facility, setFacility] =
        useState("Facility A1");

    const [dateReceived, setDateReceived] =
        useState("2026-01-10");

    const [
        medicalRecordNumber,
        setMedicalRecordNumber,
    ] = useState("MR-000001");

    const [ehrsId, setEhrsId] =
        useState("SAMPLE001");

    const [submitted, setSubmitted] =
        useState(false);

    const [dohNotes, setDohNotes] =
        useState("");

    const [screeningTypes, setScreeningTypes] =
        useState<string[]>(["autism", "dental"]);

    const [testResultSections, setTestResultSections] =
        useState<AppRowInputSection[]>(initialTestResultSections);

    /* =========================================================
       Input helper
    ========================================================= */

    const handleInputChange =
        (
            setter: React.Dispatch<
                React.SetStateAction<string>
            >
        ) =>
        (
            event: ChangeEvent<HTMLInputElement>
        ) => {
            setter(event.target.value);
        };

    /* =========================================================
       Continue
    ========================================================= */

    const handleContinue = () => {
        setSubmitted(true);

        console.log(
            "New Components Test",
            {
                source,
                firstName,
                middleName,
                lastName,
                dateOfBirth:
                    formMethods.getValues(
                        "dateOfBirth"
                    ),
                birthSex,
                hospital,
                facility,
                dateReceived,
                medicalRecordNumber,
                ehrsId,
                dohNotes,
                screeningTypes,
                testResults: testResultSections,
            }
        );
    };

    /* =========================================================
       Cancel / Reset
    ========================================================= */

    const handleCancel = () => {
        setSubmitted(false);

        setSource("Ehars");
        setFirstName("Jane");
        setMiddleName("");
        setLastName("Doe");
        setBirthSex("Female");
        setHospital("Hospital A");
        setFacility("Facility A1");
        setDateReceived("2026-01-10");
        setMedicalRecordNumber("MR-000001");
        setEhrsId("SAMPLE001");
        setDohNotes("");
        setScreeningTypes(["autism", "dental"]);
        setTestResultSections(initialTestResultSections);

        formMethods.reset({
            dateOfBirth: new Date(
                2026,
                7,
                5
            ),
            testResults: {},
        });
    };

    /* =========================================================
       Back
    ========================================================= */

    const handleBack = () => {
        console.log("Back clicked");
    };

    return (
        <FormProvider {...formMethods}>
            <div
                className="dashboard-card"
                style={{
                    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
            >
                <Container fluid>

                    {/* =================================================
                        PAGE TITLE
                    ================================================== */}

                    <div
                        style={{
                            marginBottom: "1.5rem",
                        }}
                    >
                        <h1
                            style={{
                                margin: 0,
                                marginBottom:
                                    "0.5rem",
                                fontSize:
                                    "1.5rem",
                                fontWeight: 600,
                                color: "#0f172a",
                            }}
                        >
                            New Components
                        </h1>

                        <p
                            style={{
                                margin: 0,
                                fontSize:
                                    "0.875rem",
                                color: "#64748b",
                            }}
                        >
                            Testing AppCard,
                            AppInput_New,
                            AppSelect_New,
                            AppDatePicker_New and
                            AppButton_New.
                        </p>
                    </div>

                    {/* =================================================
                        PATIENT IDENTIFICATION
                    ================================================== */}

                    <AppCard title="App Card">
                        <Row className="g-3">

                            {/* Source */}

                            <Col md={3}>
                                <AppSelect_New
                                    label="Source"
                                    name="source"
                                    value={source}
                                    onChange={(
                                        value
                                    ) =>
                                        setSource(
                                            value
                                        )
                                    }
                                    options={[
                                        {
                                            label:
                                                "Ehars",
                                            value:
                                                "Ehars",
                                        },
                                        {
                                            label:
                                                "Case Report",
                                            value:
                                                "Case Report",
                                        },
                                        {
                                            label:
                                                "Self-Reported",
                                            value:
                                                "Self-Reported",
                                        },
                                    ]}
                                />
                            </Col>

                            {/* First Name */}

                            <Col md={3}>
                                <AppInput_New
                                    name="firstName"
                                    label="First Name"
                                    value={
                                        firstName
                                    }
                                    maxLength={30}
                                    required
                                    onCustomChange={handleInputChange(
                                        setFirstName
                                    )}
                                />
                            </Col>

                            {/* Middle Name */}

                            <Col md={3}>
                                <AppInput_New
                                    name="middleName"
                                    label="Middle Name"
                                    value={
                                        middleName
                                    }
                                    onCustomChange={handleInputChange(
                                        setMiddleName
                                    )}
                                />
                            </Col>

                            {/* Last Name */}

                            <Col md={3}>
                                <AppInput_New
                                    name="lastName"
                                    label="Last Name"
                                    value={
                                        lastName
                                    }
                                    maxLength={30}
                                    required
                                    onCustomChange={handleInputChange(
                                        setLastName
                                    )}
                                />
                            </Col>

                            {/* Date of Birth */}

                            <Col md={3}>
                                <AppDatePicker_New
                                    name="dateOfBirth"
                                    label="Date of Birth"
                                    required
                                    controlId="dateOfBirth"
                                    placeholder="MM/DD/YYYY"
                                    dateFormat="MM/dd/yyyy"
                                    setMaxDate={
                                        new Date()
                                    }
                                />
                            </Col>

                            {/* Birth Sex */}

                            <Col md={3}>
                                <AppSelect_New
                                    label="Birth Sex"
                                    name="birthSex"
                                    value={
                                        birthSex
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setBirthSex(
                                            value
                                        )
                                    }
                                    options={[
                                        {
                                            label:
                                                "Female",
                                            value:
                                                "Female",
                                        },
                                        {
                                            label:
                                                "Male",
                                            value:
                                                "Male",
                                        },
                                        {
                                            label:
                                                "Unknown",
                                            value:
                                                "Unknown",
                                        },
                                    ]}
                                />
                            </Col>

                            {/* Hospital */}

                            <Col md={3}>
                                <AppSelect_New
                                    label="Routinized Testing Focus Project – Hospital"
                                    name="hospital"
                                    value={
                                        hospital
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setHospital(
                                            value
                                        )
                                    }
                                    options={[
                                        {
                                            label:
                                                "Hospital A",
                                            value:
                                                "Hospital A",
                                        },
                                        {
                                            label:
                                                "Hospital B",
                                            value:
                                                "Hospital B",
                                        },
                                        {
                                            label:
                                                "Hospital C",
                                            value:
                                                "Hospital C",
                                        },
                                    ]}
                                />
                            </Col>

                            {/* Facility */}

                            <Col md={3}>
                                <AppSelect_New
                                    label="Routinized Testing Focus Project – Facility"
                                    name="facility"
                                    value={
                                        facility
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setFacility(
                                            value
                                        )
                                    }
                                    options={[
                                        {
                                            label:
                                                "Facility A1",
                                            value:
                                                "Facility A1",
                                        },
                                        {
                                            label:
                                                "Facility A2",
                                            value:
                                                "Facility A2",
                                        },
                                        {
                                            label:
                                                "Facility B1",
                                            value:
                                                "Facility B1",
                                        },
                                    ]}
                                />
                            </Col>

                            {/* Date Received */}

                            <Col md={3}>
                                <AppInput_New
                                    name="dateReceived"
                                    label="Date Received"
                                    type="date"
                                    value={
                                        dateReceived
                                    }
                                    required
                                    onCustomChange={handleInputChange(
                                        setDateReceived
                                    )}
                                />
                            </Col>

                            {/* Medical Record */}

                            <Col md={3}>
                                <AppInput_New
                                    name="medicalRecordNumber"
                                    label="Medical record #"
                                    value={
                                        medicalRecordNumber
                                    }
                                    required
                                    onCustomChange={handleInputChange(
                                        setMedicalRecordNumber
                                    )}
                                />
                            </Col>

                            {/* EHARS ID */}

                            <Col md={3}>
                                <AppInput_New
                                    name="ehrsId"
                                    label="Source: Ehars (Ehars ID)"
                                    value={
                                        ehrsId
                                    }
                                    onCustomChange={handleInputChange(
                                        setEhrsId
                                    )}
                                />
                            </Col>

                        </Row>

                        {/* =================================================
                            FOOTER BUTTONS
                        ================================================== */}

                        <div
                            style={{
                                display: "flex",
                                justifyContent:
                                    "flex-end",
                                alignItems:
                                    "center",
                                gap: "0.75rem",
                                marginTop:
                                    "1.25rem",
                                paddingTop:
                                    "1rem",
                                borderTop:
                                    "1px solid #f1f5f9",
                            }}
                        >
                            <AppButton_New
                                variant="secondary"
                                onClick={
                                    handleCancel
                                }
                            >
                                Cancel
                            </AppButton_New>

                            <AppButton_New
                                variant="primary"
                                showArrow
                                onClick={
                                    handleContinue
                                }
                            >
                                Continue
                            </AppButton_New>
                        </div>
                    </AppCard>

                    {/* =================================================
                        APP TEST RESULTS
                    ================================================== */}

                    <div
                        style={{
                            marginTop: "1.5rem",
                        }}
                    >
                        <AppCard title="AppRowInput_New Test">
                            <AppRowInput_New
                                title="STD Test Information"
                                name="testResults"
                                sections={testResultSections}
                                onChange={setTestResultSections}
                                maxDate={new Date()}
                            />
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP DATA TABLE
                    ================================================== */}

                    <div style={{ marginTop: "1.5rem" }}>
                        <AppCard title="AppDataTable_New Test">
                            <AppDataTable_New<HealthcareLookupRecord>
                                title="Health Care Lookup Dashboard"
                                rows={lookupRecords}
                                columns={lookupColumns}
                                getRowId={(row) => row.id}
                                pageSize={10}
                                filters={[
                                    {
                                        id: "pending-under-14",
                                        label: "Pending for update under 14 days",
                                        tone: "warning",
                                        predicate: (row) => row.linkageStatus === "Pending" && (row.pendingDays ?? 0) < 14,
                                    },
                                    {
                                        id: "pending-over-14",
                                        label: "Pending for update over 14 days",
                                        tone: "danger",
                                        predicate: (row) => row.linkageStatus === "Pending" && (row.pendingDays ?? 0) >= 14,
                                    },
                                ]}
                                activity={{
                                    getItems: (row) => [
                                        { label: "Created by", value: row.createdBy },
                                        { label: "Created date", value: row.createdDate },
                                        { label: "Last updated by", value: row.updatedBy ?? "-" },
                                        { label: "Last updated date", value: row.updatedDate ?? "-" },
                                    ],
                                }}
                                onRowClick={(row) => console.log("Selected lookup record", row)}
                            />
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP MULTI SELECT
                    ================================================== */}

                    <div style={{ marginTop: "1.5rem" }}>
                        <AppCard title="AppMultiSelect_New Test">
                            <AppMultiSelect_New
                                label="No Cost Screening Type(s)"
                                name="screeningTypes"
                                options={screeningTypeOptions}
                                value={screeningTypes}
                                onChange={(values) => setScreeningTypes(values)}
                                placeholder="Select screening types"
                            />
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP TEXT BOX
                    ================================================== */}

                    <div style={{ marginTop: "1.5rem" }}>
                        <AppCard title="AppTextBox_New Test">
                            <AppTextBox_New
                                label="DOH Notes/Outcome"
                                name="dohNotes"
                                value={dohNotes}
                                onValueChange={setDohNotes}
                                maxLength={1500}
                                rows={4}
                                placeholder="Enter notes or outcome"
                            />
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP BUTTON TEST
                    ================================================== */}

                    <div
                        style={{
                            marginTop: "1.5rem",
                        }}
                    >
                        <AppCard title="AppButton_New Test">
                            <div
                                style={{
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    gap: "1rem",
                                    flexWrap:
                                        "wrap",
                                }}
                            >

                                {/* Primary */}

                                <AppButton_New
                                    variant="primary"
                                    showArrow
                                    onClick={() =>
                                        console.log(
                                            "Primary clicked"
                                        )
                                    }
                                >
                                    Continue
                                </AppButton_New>

                                {/* Secondary */}

                                <AppButton_New
                                    variant="secondary"
                                    onClick={() =>
                                        console.log(
                                            "Secondary clicked"
                                        )
                                    }
                                >
                                    Cancel
                                </AppButton_New>

                                {/* Tertiary / Back */}

                                <AppButton_New
                                    variant="tertiary"
                                    showBackArrow
                                    onClick={
                                        handleBack
                                    }
                                >
                                    Back
                                </AppButton_New>

                                {/* Primary Disabled */}

                                <AppButton_New
                                    variant="primary"
                                    showArrow
                                    disabled
                                >
                                    Continue
                                </AppButton_New>

                                {/* Secondary Disabled */}

                                <AppButton_New
                                    variant="secondary"
                                    disabled
                                >
                                    Cancel
                                </AppButton_New>

                                {/* Tertiary Disabled */}

                                <AppButton_New
                                    variant="tertiary"
                                    showBackArrow
                                    disabled
                                >
                                    Back
                                </AppButton_New>

                                {/* Loading */}

                                <AppButton_New
                                    variant="primary"
                                    loading
                                >
                                    Continue
                                </AppButton_New>

                            </div>
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP INPUT TEST
                    ================================================== */}

                    <div
                        style={{
                            marginTop: "1.5rem",
                        }}
                    >
                        <AppCard title="AppInput_New Test">
                            <Row className="g-3">

                                <Col md={6}>
                                    <AppInput_New
                                        name="normalInput"
                                        label="Normal Input"
                                        placeholder="Enter something..."
                                    />
                                </Col>

                                <Col md={6}>
                                    <AppInput_New
                                        name="requiredInput"
                                        label="Required Input"
                                        placeholder="This field is required"
                                        required
                                    />
                                </Col>

                                <Col md={6}>
                                    <AppInput_New
                                        name="hintInput"
                                        label="Input With Hint"
                                        placeholder="Type here..."
                                        hint="This is an example hint."
                                    />
                                </Col>

                                <Col md={6}>
                                    <AppInput_New
                                        name="errorInput"
                                        label="Input With Error"
                                        placeholder="Enter value"
                                        error="This field contains an error."
                                    />
                                </Col>

                                <Col md={6}>
                                    <AppInput_New
                                        name="disabledInput"
                                        label="Disabled Input"
                                        value="Disabled value"
                                        disabled
                                    />
                                </Col>

                                <Col md={6}>
                                    <AppInput_New
                                        name="readOnlyInput"
                                        label="Read Only Input"
                                        value="Read only value"
                                        readOnly
                                    />
                                </Col>

                            </Row>
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP SELECT TEST
                    ================================================== */}

                    <div
                        style={{
                            marginTop: "1.5rem",
                        }}
                    >
                        <AppCard title="AppSelect_New Test">
                            <Row className="g-3">

                                <Col md={6}>
                                    <AppSelect_New
                                        label="Normal Select"
                                        name="testSource"
                                        value={
                                            source
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            setSource(
                                                value
                                            )
                                        }
                                        options={[
                                            {
                                                label:
                                                    "Ehars",
                                                value:
                                                    "Ehars",
                                            },
                                            {
                                                label:
                                                    "Case Report",
                                                value:
                                                    "Case Report",
                                            },
                                            {
                                                label:
                                                    "Self-Reported",
                                                value:
                                                    "Self-Reported",
                                            },
                                        ]}
                                    />
                                </Col>

                                <Col md={6}>
                                    <AppSelect_New
                                        label="Another Select"
                                        name="testSex"
                                        value={
                                            birthSex
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            setBirthSex(
                                                value
                                            )
                                        }
                                        options={[
                                            {
                                                label:
                                                    "Female",
                                                value:
                                                    "Female",
                                            },
                                            {
                                                label:
                                                    "Male",
                                                value:
                                                    "Male",
                                            },
                                            {
                                                label:
                                                    "Unknown",
                                                value:
                                                    "Unknown",
                                            },
                                        ]}
                                    />
                                </Col>

                            </Row>

                            <div
                                style={{
                                    marginTop:
                                        "0.75rem",
                                    fontSize:
                                        "0.875rem",
                                    color:
                                        "#475569",
                                }}
                            >
                                <strong>
                                    Selected source:
                                </strong>{" "}
                                {source ||
                                    "Nothing selected"}
                            </div>
                        </AppCard>
                    </div>

                    {/* =================================================
                        APP DATE PICKER TEST
                    ================================================== */}

                    <div
                        style={{
                            marginTop: "1.5rem",
                        }}
                    >
                        <AppCard title="AppDatePicker_New Test">
                            <Row className="g-3">

                                <Col md={4}>
                                    <AppDatePicker_New
                                        name="dateOfBirth"
                                        label="Date of Birth"
                                        required
                                        controlId="testDateOfBirth"
                                        placeholder="MM/DD/YYYY"
                                        dateFormat="MM/dd/yyyy"
                                        setMaxDate={
                                            new Date()
                                        }
                                    />
                                </Col>

                            </Row>
                        </AppCard>
                    </div>

                    {/* =================================================
                        SUBMITTED DATA
                    ================================================== */}

                    {submitted && (
                        <div
                            style={{
                                marginTop:
                                    "1.5rem",
                            }}
                        >
                            <AppCard title="Submitted Values">
                                <pre
                                    style={{
                                        margin: 0,
                                        padding:
                                            "1rem",
                                        overflowX:
                                            "auto",
                                        background:
                                            "#0f172a",
                                        color:
                                            "#e2e8f0",
                                        borderRadius:
                                            "0.5rem",
                                        fontSize:
                                            "0.8125rem",
                                        lineHeight:
                                            "1.5rem",
                                    }}
                                >
                                    {JSON.stringify(
                                        {
                                            source,
                                            firstName,
                                            middleName,
                                            lastName,
                                            dateOfBirth:
                                                formMethods.getValues(
                                                    "dateOfBirth"
                                                ),
                                            birthSex,
                                            hospital,
                                            facility,
                                            dateReceived,
                                            medicalRecordNumber,
                                            ehrsId,
                                            dohNotes,
                                            screeningTypes,
                                            testResults:
                                                testResultSections,
                                        },
                                        null,
                                        2
                                    )}
                                </pre>
                            </AppCard>
                        </div>
                    )}

                </Container>
            </div>
        </FormProvider>
    );
};

export default NewComponent;
